import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, SimpleGrid, Text, VStack, Heading, Select, HStack, Spinner, Badge,
  Progress, Button, useToast, useDisclosure,
} from '@chakra-ui/react';
import { DownloadIcon } from '@chakra-ui/icons';
import { riskApi, departmentApi, lookupApi, reportApi } from '../services/api';
import { DashboardData, HeatMapData, Department, FiscalYear, DashboardAnalytics, IcAcCoverage as IcAcCoverageData, Target } from '../types';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../hooks/useLanguage';
import HeatMap from '../components/HeatMap';
import RiskDetailModal from '../components/RiskDetailModal';
import PowerBiDashboard from '../components/PowerBiDashboard';
import IcAcCoverage from '../components/IcAcCoverage';

const SummaryCard: React.FC<{ label: string; value: number; bg: string; color: string; sublabel?: string }> = ({
  label, value, bg, color, sublabel,
}) => (
  <Box bg={bg} color={color} p={4} borderRadius="md" textAlign="center" boxShadow="md">
    <Text fontSize="xs" fontWeight="bold" letterSpacing="wider" mb={1}>{label}</Text>
    <Text fontSize="3xl" fontWeight="black">{value}</Text>
    {sublabel && <Text fontSize="xs" opacity={0.8}>{sublabel}</Text>}
  </Box>
);

const Dashboard: React.FC = () => {
  const { isAdmin, isManager, isUser, isGuest, user } = useAuth();
  const { t } = useLanguage();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [heatMapData, setHeatMapData] = useState<HeatMapData | null>(null);
  const [analyticsData, setAnalyticsData] = useState<DashboardAnalytics | null>(null);
  const [icAcData, setIcAcData] = useState<IcAcCoverageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState<string | null>(null);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [fiscalYears, setFiscalYears] = useState<FiscalYear[]>([]);
  const [selectedUnitId, setSelectedUnitId] = useState('');
  const [selectedFiscalYear, setSelectedFiscalYear] = useState('');
  const [selectedErmLevel, setSelectedErmLevel] = useState('');
  const [selectedRiskCode, setSelectedRiskCode] = useState<string | null>(null);
  const [departmentTargets, setDepartmentTargets] = useState<Target[]>([]);
  const { isOpen: isRiskDetailOpen, onOpen: onRiskDetailOpen, onClose: onRiskDetailClose } = useDisclosure();
  const toast = useToast();

  useEffect(() => {
    Promise.allSettled([
      departmentApi.getAll(),
      lookupApi.getFiscalYears(),
    ]).then(([deptRes, fyRes]) => {
      if (deptRes.status === 'fulfilled') setDepartments(deptRes.value.data);
      if (fyRes.status === 'fulfilled') setFiscalYears(fyRes.value.data);
    });
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: { unit_id?: string; fiscal_year_id?: number; erm_level?: string } = {};
      if (selectedUnitId) params.unit_id = selectedUnitId;
      if (selectedFiscalYear) params.fiscal_year_id = Number(selectedFiscalYear);
      if (selectedErmLevel) params.erm_level = selectedErmLevel;

      const apiCalls: Promise<any>[] = [
        riskApi.getDashboard(params),
        riskApi.getHeatMap(params),
        riskApi.getDashboardAnalytics(params),
        riskApi.getIcAcCoverage(params),
      ];

      const [dashboardRes, heatMapRes, analyticsRes, icAcRes, targetsRes] = await Promise.allSettled(apiCalls);
      if (dashboardRes.status === 'fulfilled') setDashboardData(dashboardRes.value.data);
      if (heatMapRes.status === 'fulfilled') setHeatMapData(heatMapRes.value.data);
      if (analyticsRes.status === 'fulfilled') setAnalyticsData(analyticsRes.value.data);
      if (icAcRes.status === 'fulfilled') setIcAcData(icAcRes.value.data);

      // Fetch department targets when a department is selected
      if (selectedUnitId) {
        try {
          const targetsRes = await departmentApi.getTargets(selectedUnitId);
          setDepartmentTargets(targetsRes.data);
        } catch {
          setDepartmentTargets([]);
        }
      } else {
        setDepartmentTargets([]);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedUnitId, selectedFiscalYear, selectedErmLevel]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExport = async (docType: 'doc3' | 'doc4', format: 'excel' | 'pdf' | 'excel-zip' | 'word' = 'excel') => {
    if (!selectedFiscalYear) {
      toast({ title: t('docs.selectFiscalYear'), status: 'warning' });
      return;
    }
    setExporting(docType);
    try {
      const params: { unit_id?: string; fiscal_year_id: number } = {
        fiscal_year_id: Number(selectedFiscalYear),
      };
      if (selectedUnitId) params.unit_id = selectedUnitId;

      const fyYear = fiscalYears.find(fy => String(fy.id) === selectedFiscalYear)?.year || selectedFiscalYear;

      let res;
      let filename = '';

      if (format === 'excel-zip') {
        res = docType === 'doc3' ? await reportApi.exportDoc3ExcelZip(params) : await reportApi.exportDoc4ExcelZip(params);
        filename = `doc3_${fyYear}.zip`;
      } else if (format === 'word') {
        res = docType === 'doc3' ? await reportApi.exportDoc3Word(params) : await reportApi.exportDoc4Word(params);
        filename = `doc3_${fyYear}.docx`;
      } else if (format === 'pdf') {
        res = docType === 'doc3' ? await reportApi.exportDoc3Pdf(params) : await reportApi.exportDoc4Pdf(params);
        filename = `ERM_doc3_${fyYear}.pdf`;
      } else {
        res = docType === 'doc3' ? await reportApi.exportDoc3(params) : await reportApi.exportDoc4(params);
        filename = `ERM_doc3_${fyYear}.xlsx`;
      }

      // Fix filename for doc4
      if (docType === 'doc4') {
        if (format === 'excel-zip') filename = `doc4_${fyYear}.zip`;
        else if (format === 'word') filename = `doc4_${fyYear}.docx`;
        else if (format === 'pdf') filename = `ERM_doc4_${fyYear}.pdf`;
        else filename = `ERM_doc4_${fyYear}.xlsx`;
      }

      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast({ title: t('docs.exportSuccess'), status: 'success' });
    } catch (error) {
      toast({ title: t('docs.exportError'), status: 'error' });
    } finally {
      setExporting(null);
    }
  };

  const icCompletion = dashboardData ? (dashboardData.total_risks > 0 ? Math.round((dashboardData.total_ic / dashboardData.total_risks) * 100) : 0) : 0;
  const acCompletion = dashboardData ? (dashboardData.total_risks > 0 ? Math.round((dashboardData.total_ac / dashboardData.total_risks) * 100) : 0) : 0;

  const handleRiskCodeClick = useCallback((code: string) => {
    setSelectedRiskCode(code);
    onRiskDetailOpen();
  }, [onRiskDetailOpen]);

  return (
    <Box>
      <VStack spacing={6} align="stretch">
        <HStack justifyContent="space-between" alignItems="center">
          <Heading size="lg" color="blue.600">{t('dashboard.title')}</Heading>
          {loading && <Spinner size="sm" color="blue.500" />}
        </HStack>

        <HStack spacing={4} flexWrap="wrap">
          <Select placeholder={t('dashboard.allDepartments')} value={selectedUnitId} onChange={(e) => setSelectedUnitId(e.target.value)} maxW="320px" size="sm">
            {departments.map((d) => (
              <option key={d.unit_id} value={d.unit_id}>
                {d.work_unit ? `${d.department_name} - ${d.work_unit}` : d.department_name}
              </option>
            ))}
          </Select>
          <Select placeholder={t('dashboard.allFiscalYears')} value={selectedFiscalYear} onChange={(e) => setSelectedFiscalYear(e.target.value)} maxW="200px" size="sm">
            {fiscalYears.map((fy) => (<option key={fy.id} value={fy.id}>{fy.year}</option>))}
          </Select>
          <Select placeholder={t('dashboard.allLevels')} value={selectedErmLevel} onChange={(e) => setSelectedErmLevel(e.target.value)} maxW="200px" size="sm">
            <option value="department">{t('dashboard.levelDepartment')}</option>
            <option value="faculty">{t('dashboard.levelFaculty')}</option>
          </Select>
          <Box ml="auto">
            <HStack spacing={2}>
              {!isGuest && (isAdmin || isManager) && (
                <>
                  <Button size="sm" colorScheme="blue" leftIcon={<DownloadIcon />} isLoading={exporting === 'doc3'} onClick={() => handleExport('doc3', 'excel-zip')}>
                    เอกสารหมายเลข3.xlsx Zip
                  </Button>
                  <Button size="sm" colorScheme="blue" variant="outline" leftIcon={<DownloadIcon />} isLoading={exporting === 'doc3'} onClick={() => handleExport('doc3', 'word')}>
                    เอกสารหมายเลข3.docx
                  </Button>
                  <Button size="sm" colorScheme="blue" variant="outline" leftIcon={<DownloadIcon />} isLoading={exporting === 'doc3'} onClick={() => handleExport('doc3', 'pdf')}>
                    เอกสารหมายเลข3.pdf
                  </Button>
                  <Button size="sm" colorScheme="red" leftIcon={<DownloadIcon />} isLoading={exporting === 'doc4'} onClick={() => handleExport('doc4', 'excel-zip')}>
                    เอกสารหมายเลข4.xlsx Zip
                  </Button>
                  <Button size="sm" colorScheme="red" variant="outline" leftIcon={<DownloadIcon />} isLoading={exporting === 'doc4'} onClick={() => handleExport('doc4', 'word')}>
                    เอกสารหมายเลข4.docx
                  </Button>
                  <Button size="sm" colorScheme="red" variant="outline" leftIcon={<DownloadIcon />} isLoading={exporting === 'doc4'} onClick={() => handleExport('doc4', 'pdf')}>
                    เอกสารหมายเลข4.pdf
                  </Button>
                </>
              )}
              {!isGuest && isUser && !isAdmin && !isManager && (
                <>
                  <Button size="sm" colorScheme="blue" leftIcon={<DownloadIcon />} isLoading={exporting === 'doc3'} onClick={() => handleExport('doc3', 'pdf')}>
                    เอกสารหมายเลข3.pdf
                  </Button>
                  <Button size="sm" colorScheme="red" leftIcon={<DownloadIcon />} isLoading={exporting === 'doc4'} onClick={() => handleExport('doc4', 'pdf')}>
                    เอกสารหมายเลข4.pdf
                  </Button>
                </>
              )}
            </HStack>
          </Box>
        </HStack>

        {dashboardData && (
          <>
            <SimpleGrid columns={{ base: 2, md: 3, lg: 7 }} spacing={4}>
              <SummaryCard label={t('dashboard.totalRisks')} value={dashboardData.total_risks} bg="#0F2C59" color="white" />
              <SummaryCard label={t('dashboard.veryHighRisks')} value={dashboardData.very_high_risks} bg="#D9383A" color="white" />
              <SummaryCard label={t('dashboard.highRisks')} value={dashboardData.high_risks} bg="#E59834" color="white" />
              <SummaryCard label={t('dashboard.mediumRisks')} value={dashboardData.medium_risks} bg="#F4D03F" color="black" />
              <SummaryCard label={t('dashboard.lowRisks')} value={dashboardData.low_risks} bg="#2ECC71" color="white" />
              <SummaryCard label={t('dashboard.ermLevel')} value={dashboardData.faculty_level} bg="#7D3C98" color="white" sublabel={t('dashboard.facultyLevel')} />
              <SummaryCard label={t('dashboard.deptLevel')} value={dashboardData.department_level} bg="#2C3E50" color="white" sublabel={t('dashboard.departmentLevel')} />
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
              <Box bg="white" p={4} borderRadius="lg" boxShadow="sm" borderLeft="4px solid" borderColor="blue.500">
                <Text fontSize="sm" fontWeight="bold" color="blue.600">{t('dashboard.internalControls')}</Text>
                <Text fontSize="2xl" fontWeight="black">{dashboardData.total_ic}</Text>
                <Text fontSize="xs" color="gray.500">{t('dashboard.coverageRisks', { a: dashboardData.total_ic, b: dashboardData.total_risks })}</Text>
                <Progress value={icCompletion} colorScheme="green" size="sm" mt={2} borderRadius="full" />
                <Text fontSize="xs" color="gray.500" mt={1}>{t('dashboard.percentRisksPlanned', { p: icCompletion })}</Text>
              </Box>

              <Box bg="white" p={4} borderRadius="lg" boxShadow="sm" borderLeft="4px solid" borderColor="yellow.500">
                <Text fontSize="sm" fontWeight="bold" color="yellow.600">{t('dashboard.additionalControls')}</Text>
                <Text fontSize="2xl" fontWeight="black">{dashboardData.total_ac}</Text>
                <Text fontSize="xs" color="gray.500">{t('dashboard.coverageRisks', { a: dashboardData.total_ac, b: dashboardData.total_risks })}</Text>
                <Progress value={acCompletion} colorScheme="yellow" size="sm" mt={2} borderRadius="full" />
                <Text fontSize="xs" color="gray.500" mt={1}>{t('dashboard.percentRisksPlanned', { p: acCompletion })}</Text>
              </Box>

              <Box bg="white" p={4} borderRadius="lg" boxShadow="sm" borderLeft="4px solid" borderColor="purple.500">
                <Text fontSize="sm" fontWeight="bold" color="purple.600">{t('dashboard.kriIndicators')}</Text>
                <Text fontSize="2xl" fontWeight="black">{dashboardData.total_kri}</Text>
                <Text fontSize="xs" color="gray.500">{t('dashboard.kriTracking')}</Text>
              </Box>
            </SimpleGrid>
          </>
        )}

        {departmentTargets.length > 0 && (
          <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
            <Heading size="md" mb={4} color="gray.700">{t('dashboard.departmentTargets')}</Heading>
            <VStack spacing={3} align="stretch">
              {departmentTargets.map((target) => (
                <Box key={target.id} p={3} bg="blue.50" borderRadius="md">
                  <Text fontSize="sm" fontWeight="bold" color="blue.700">เป้าหมายที่ {target.target_no}</Text>
                  <Text fontSize="sm" color="gray.700">{target.target_description}</Text>
                </Box>
              ))}
            </VStack>
          </Box>
        )}

        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
          <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
            <Heading size="md" mb={4} color="gray.700">{t('dashboard.heatMap')}</Heading>
            {heatMapData && <HeatMap data={heatMapData} onRiskCodeClick={handleRiskCodeClick} />}
          </Box>

          <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
            <Heading size="md" mb={4} color="gray.700">{t('dashboard.riskSummary')}</Heading>
            {dashboardData && (
              <VStack spacing={4} align="stretch">
                <Box p={4} bg="red.50" borderRadius="md">
                  <Text fontWeight="bold" color="red.600">{t('dashboard.veryHighRisks')}: {dashboardData.very_high_risks}</Text>
                  <Text fontSize="sm" color="gray.600">{t('dashboard.veryHighImmediate')}</Text>
                </Box>
                <Box p={4} bg="orange.50" borderRadius="md">
                  <Text fontWeight="bold" color="orange.600">{t('dashboard.highRisks')}: {dashboardData.high_risks}</Text>
                  <Text fontSize="sm" color="gray.600">{t('dashboard.highImmediate')}</Text>
                </Box>
                <Box p={4} bg="yellow.50" borderRadius="md">
                  <Text fontWeight="bold" color="yellow.700">{t('dashboard.mediumRisks')}: {dashboardData.medium_risks}</Text>
                  <Text fontSize="sm" color="gray.600">{t('dashboard.mediumMonitor')}</Text>
                </Box>
                <Box p={4} bg="green.50" borderRadius="md">
                  <Text fontWeight="bold" color="green.600">{t('dashboard.lowRisks')}: {dashboardData.low_risks}</Text>
                  <Text fontSize="sm" color="gray.600">{t('dashboard.lowAcceptable')}</Text>
                </Box>
              </VStack>
            )}
          </Box>
        </SimpleGrid>

        {analyticsData && (
          <Box borderTop="4px solid" borderColor="blue.200" pt={6}>
            <PowerBiDashboard data={analyticsData} heatMap={heatMapData} showDepartmentCharts={isAdmin || isManager} />
          </Box>
        )}

        {icAcData && (
          <Box borderTop="4px solid" borderColor="teal.200" pt={6}>
            <IcAcCoverage data={icAcData} />
          </Box>
        )}
      </VStack>

      <RiskDetailModal
        riskCode={selectedRiskCode}
        fiscalYearId={selectedFiscalYear ? Number(selectedFiscalYear) : undefined}
        isOpen={isRiskDetailOpen}
        onClose={onRiskDetailClose}
      />
    </Box>
  );
};

export default Dashboard;
